/**
 * Pure function to build save payloads for EditSentDropModal.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import {
  shortlinkPick,
  type ClickIdStructureValue,
  type DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';
import type { EditSentDropPayload } from './EditSentDropModal.types';

const LEAVE_TLD_UNCHANGED = '__unchanged__';

export interface SentBulkPayloadResolvers {
  campaignWiserId?: (d: PreparedDrop) => number;
  routeId?: (d: PreparedDrop, index: number) => number | '';
  shortLinkTld?: (d: PreparedDrop, index: number) => string;
}

interface BuildParams {
  drops: PreparedDrop[];
  isBulk: boolean;
  title: string;
  selectedSenderNames: string[];
  selectedSmsTexts: string[];
  wiserId: number;
  routeId: number | '';
  tldValue: string;
  forceSend: boolean | 'unchanged';
  useOnlyNewDomains: boolean | 'unchanged';
  /** Editable on sent drops (SMSWiser PATCH accepts them); omitted = 'unchanged'. */
  clickIdStructure?: ClickIdStructureValue;
  domainScheme?: DomainSchemeValue;
  perDropSmsTexts?: string[][] | null;
  perDropSenderNames?: string[][] | null;
  resolvers?: SentBulkPayloadResolvers;
}

function applyShortlinkPicks(p: EditSentDropPayload, params: BuildParams): void {
  const clickId = params.clickIdStructure ? shortlinkPick(params.clickIdStructure) : undefined;
  if (clickId) p.clickIdStructure = clickId;
  const scheme = params.domainScheme ? shortlinkPick(params.domainScheme) : undefined;
  if (scheme) p.domainScheme = scheme;
}

export function buildSentDropPayloads(params: BuildParams): EditSentDropPayload[] {
  const {
    drops,
    isBulk,
    title,
    selectedSenderNames,
    selectedSmsTexts,
    wiserId,
    routeId,
    tldValue,
    forceSend,
    perDropSmsTexts,
    perDropSenderNames,
    resolvers,
  } = params;
  const payloads: EditSentDropPayload[] = [];

  drops.forEach((d, i) => {
    if (d.sentDropId == null) return;
    const p: EditSentDropPayload = {
      dropId: d.sentDropId,
      uniqueId: d.sentDropUniqueId ?? undefined,
    };

    if (!isBulk) {
      p.title = title.trim() || undefined;
      p.senderName = selectedSenderNames.join(',');
      p.smsText = selectedSmsTexts;
      p.campaign = String(wiserId);
      p.smsGatewayRoute = routeId !== '' ? String(routeId) : undefined;
      p.shortLinkTld = tldValue || undefined;
      if (forceSend === true || forceSend === false) p.forceSend = forceSend;
    } else {
      const perS = perDropSenderNames?.[i];
      const perT = perDropSmsTexts?.[i];
      const eSenders =
        perS && perS.length > 0
          ? perS.join(',')
          : selectedSenderNames.length > 0
            ? selectedSenderNames.join(',')
            : (d.senderNames ?? []).join(',');
      const eTexts =
        perT && perT.length > 0
          ? perT
          : selectedSmsTexts.length > 0
            ? selectedSmsTexts
            : (d.smsTexts ?? []).filter((t) => String(t).trim());
      if (!eSenders || eTexts.length === 0) return;
      p.title = (d.sentTitle ?? d.titleBase ?? '').trim() || undefined;
      p.senderName = eSenders;
      p.smsText = eTexts;
      const campaignId = resolvers?.campaignWiserId?.(d) ?? d.wiserId;
      p.campaign = String(campaignId);
      const effRoute =
        resolvers?.routeId != null ? resolvers.routeId(d, i) : routeId !== '' ? routeId : (d.routeId ?? '');
      p.smsGatewayRoute =
        effRoute !== '' && effRoute !== undefined && effRoute != null ? String(effRoute) : String(d.routeId ?? '');
      p.shortLinkTld =
        resolvers?.shortLinkTld != null
          ? resolvers.shortLinkTld(d, i)
          : tldValue && tldValue !== LEAVE_TLD_UNCHANGED
            ? tldValue
            : (d.shortLinkTldValue ?? '');
      if (forceSend === true || forceSend === false) p.forceSend = forceSend;
    }
    applyShortlinkPicks(p, params);

    const hasChange =
      p.title != null ||
      (p.senderName && p.senderName !== '') ||
      p.smsText?.some((t) => t.trim()) ||
      (p.smsGatewayRoute && p.smsGatewayRoute !== '') ||
      (p.shortLinkTld && p.shortLinkTld !== '') ||
      p.forceSend != null ||
      p.clickIdStructure != null ||
      p.domainScheme != null;
    if (hasChange) payloads.push(p);
  });

  return payloads;
}
